import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHead } from '@unhead/vue'
import { getLocaleConfig, supportedLocales } from '@/locales/config'
import { useLocaleRoute } from '@/composables/useLocaleRoute'

/**
 * Set i18n-aware <head> tags: lang, dir, title, description, og:locale.
 * Call once per page view (typically in App.vue or a layout component).
 */
export function useI18nHead() {
  const { locale, t } = useI18n()
  const { localePath } = useLocaleRoute()

  const config = computed(() => getLocaleConfig(locale.value))

  useHead({
    htmlAttrs: {
      lang: () => locale.value,
      dir: () => config.value.dir,
    },
    title: () => t('meta.title'),
    meta: [
      { name: 'description', content: () => t('meta.description') },
      { property: 'og:locale', content: () => locale.value },
      ...supportedLocales
        .filter((l) => l.code !== locale.value)
        .map((l) => ({
          property: 'og:locale:alternate',
          content: l.code,
        })),
    ],
    link: [
      ...supportedLocales.map((l) => ({
        rel: 'alternate',
        hreflang: l.code,
        href: () => {
          const path = localePath('/challenges')
          return path.replace(`/${locale.value}/`, `/${l.code}/`)
        },
      })),
    ],
  })
}
